'use client';

import { type ReactNode, useEffect, useRef } from 'react';

import type { ViewContext } from '../../core/constants';
import type { ViewMetadata } from '../../core/types';
import { usePersonalizationTracker } from '../PersonalizationProvider';

/**
 * Props for TrackView component.
 *
 * @property {string} productId - Unique product identifier
 * @property {ViewContext} context - Context where the view occurs
 * @property {ViewMetadata} [metadata] - Optional view metadata
 * @property {ReactNode} children - Child components to render
 */
interface TrackViewProps {
  productId: string;
  context: ViewContext;
  metadata?: ViewMetadata;
  children: ReactNode;
}

/**
 * Component that automatically tracks product view events.
 * Tracks once on mount and when productId changes.
 *
 * @param {TrackViewProps} props - Component props
 *
 * @example
 * // Basic usage
 * <TrackView productId="prod_123" context="product_detail">
 *   <ProductDetail />
 * </TrackView>
 *
 * @example
 * // With metadata
 * <TrackView
 *   productId="prod_123"
 *   context="search_results"
 *   metadata={{
 *     searchQuery: 'vitamin c serum',
 *     position: 1
 *   }}
 * >
 *   <ProductCard />
 * </TrackView>
 */
export function TrackView({ productId, context, metadata, children }: TrackViewProps) {
  const tracker = usePersonalizationTracker();
  const hasLoggedRef = useRef(false);
  const prevProductIdRef = useRef(productId);
  const metadataRef = useRef(metadata);
  metadataRef.current = metadata;

  useEffect(() => {
    if (!productId) {
      return;
    }

    if (!hasLoggedRef.current) {
      tracker.view(productId, context, metadataRef.current);
      hasLoggedRef.current = true;
      prevProductIdRef.current = productId;
      return;
    }

    if (prevProductIdRef.current !== productId) {
      tracker.view(productId, context, metadataRef.current);
      prevProductIdRef.current = productId;
    }
  }, [tracker, productId, context]);

  return <>{children}</>;
}
